import React from 'react';
import { Link } from 'react-router-dom';
import { TopCode } from './useFullReportViewModel';
import { GroupInfoData } from '../../../data/catData';
import { FileText, Briefcase, ExternalLink } from 'lucide-react';

import { CareerOrientationGroupWithQuestions } from '@vsat/shared/lib/api';

type TopCardsProps = {
  top3Codes: TopCode[];
  top3Scores: { score: number }[];
  displayGroups: CareerOrientationGroupWithQuestions[];
  selectedLevel?: 'THCS' | 'THPT';
  groupInfoMap?: Record<string, GroupInfoData>; // Keep for backward compat, but unused
};

// Color configuration matching HTML design exactly
const colorConfig: Record<
  string,
  {
    text: string;
    bg: string;
    bgLight: string;
    border: string;
    borderDashed: string;
    marker: string;
    pattern: string;
  }
> = {
  orange: {
    text: 'text-[#F97316]',
    bg: 'bg-[#F97316]',
    bgLight: 'bg-[#F97316]/5 dark:bg-[#F97316]/10',
    border: 'border-[#F97316]/10 dark:border-[#F97316]/20',
    borderDashed: 'border-[#F97316]/30 dark:border-[#F97316]/8',
    marker: 'marker:text-[#F97316]',
    pattern: 'pattern-artistic',
  },
  green: {
    text: 'text-[#16a34a]',
    bg: 'bg-[#16a34a]',
    bgLight: 'bg-[#16a34a]/5 dark:bg-[#16a34a]/10',
    border: 'border-[#16a34a]/10 dark:border-[#16a34a]/20',
    borderDashed: 'border-[#16a34a]/30 dark:border-[#16a34a]/8',
    marker: 'marker:text-[#16a34a]',
    pattern: 'pattern-social',
  },
  blue: {
    text: 'text-[#0369A1]',
    bg: 'bg-[#0369A1]',
    bgLight: 'bg-[#0369A1]/5 dark:bg-[#0369A1]/10',
    border: 'border-[#0369A1]/10 dark:border-[#0369A1]/20',
    borderDashed: 'border-[#0369A1]/30 dark:border-[#0369A1]/8',
    marker: 'marker:text-[#0369A1]',
    pattern: 'pattern-investigative',
  },
  purple: {
    text: 'text-[#9333ea]',
    bg: 'bg-[#9333ea]',
    bgLight: 'bg-[#9333ea]/5 dark:bg-[#9333ea]/10',
    border: 'border-[#9333ea]/10 dark:border-[#9333ea]/20',
    borderDashed: 'border-[#9333ea]/30 dark:border-[#9333ea]/8',
    marker: 'marker:text-[#9333ea]',
    pattern: '',
  },
  red: {
    text: 'text-[#dc2626]',
    bg: 'bg-[#dc2626]',
    bgLight: 'bg-[#dc2626]/5 dark:bg-[#dc2626]/10',
    border: 'border-[#dc2626]/10 dark:border-[#dc2626]/20',
    borderDashed: 'border-[#dc2626]/30 dark:border-[#dc2626]/8',
    marker: 'marker:text-[#dc2626]',
    pattern: '',
  },
  gray: {
    text: 'text-gray-600 dark:text-gray-400',
    bg: 'bg-gray-600',
    bgLight: 'bg-gray-100 dark:bg-gray-800',
    border: 'border-gray-200 dark:border-gray-700',
    borderDashed: 'border-gray-300 dark:border-gray-600',
    marker: 'marker:text-gray-600',
    pattern: '',
  },
};

// Pattern styles matching HTML design
const patternStyles: Record<string, React.CSSProperties> = {
  'pattern-artistic': {
    backgroundImage:
      'radial-gradient(circle at top left, rgba(249,115,22,0.15) 0%, transparent 70%), radial-gradient(circle at bottom right, rgba(249,115,22,0.15) 0%, transparent 70%)',
  },
  'pattern-social': {
    backgroundImage:
      'radial-gradient(circle at top right, rgba(22,163,74,0.15) 0%, transparent 70%), radial-gradient(circle at bottom left, rgba(22,163,74,0.15) 0%, transparent 70%)',
  },
  'pattern-investigative': {
    backgroundImage: 'radial-gradient(circle at center, rgba(3,105,161,0.15) 0%, transparent 70%)',
  },
};

// Map Holland code to GROUP_INFO key
const codeToGroupKey: Record<string, string> = {
  R: 'group1', // Kỹ thuật - Realistic
  I: 'group2', // Nghiên cứu - Investigative
  A: 'group3', // Nghệ thuật - Artistic
  S: 'group4', // Xã hội - Social
  E: 'group5', // Quản lý - Enterprising
  C: 'group6', // Nghiệp vụ - Conventional
};

// Helper function to generate career slug for links
const generateCareerSlug = (career: string): string => {
  return career
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
};

export function TopCards({
  top3Codes,
  top3Scores,
  displayGroups,
  selectedLevel = 'THPT',
  groupInfoMap = {},
}: TopCardsProps) {
  // Determine terminology based on level
  const isThcs = selectedLevel === 'THCS';
  const sectionTitle = isThcs ? 'Gợi ý nhóm nghề phù hợp' : 'Gợi ý nhóm ngành phù hợp';
  const sectionNote = isThcs
    ? '* Các nhóm nghề được sắp xếp theo mức độ phù hợp từ cao xuống thấp.'
    : '* Các nhóm ngành được sắp xếp theo mức độ phù hợp từ cao xuống thấp.';

  return (
    <div className='grid grid-cols-1 md:grid-cols-1 lg:grid-cols-3 gap-6 mb-8 items-stretch'>
      {top3Codes.map((item, idx) => {
        const colorKey = item.color as keyof typeof colorConfig;

        // Map Holland code to GROUP_INFO key
        const groupKey = codeToGroupKey[item.code] || 'group1';

        // FIX: More robust group lookup using multiple matching strategies
        // 1. Match by code (e.g., 'group1', 'group5')
        // 2. Match by group ID from the top3Codes data
        // 3. Match by name (normalized)
        const dynamicGroup = displayGroups.find(g => {
          // Primary: match by code
          if (g.code === groupKey) return true;
          // Fallback 1: match by group id from item.group
          if (item.group && g.id === item.group.id) return true;
          // Fallback 2: match by name (case-insensitive partial match)
          const itemName = (item.shortName || item.name || '').toUpperCase();
          const groupName = (g.name || '').toUpperCase();
          if (
            itemName &&
            groupName &&
            (groupName.includes(itemName) || itemName.includes(groupName.split(' ')[0]))
          )
            return true;
          return false;
        });

        const groupInfo = groupInfoMap[groupKey];

        // Use fullDescDetails which has title+content structure
        // Also check if displayGroups[idx] has the data as fallback (ordered by rank)
        const fallbackGroup = displayGroups[idx];
        const fullDescDetails =
          dynamicGroup?.fullDescDetails ||
          fallbackGroup?.fullDescDetails ||
          groupInfo?.fullDescDetails ||
          [];

        // Get career groups based on level
        const careerGroups = isThcs
          ? dynamicGroup?.thcsCareerGroups ||
          fallbackGroup?.thcsCareerGroups ||
          groupInfo?.thcsCareerGroups ||
          []
          : dynamicGroup?.thptMajorGroups ||
          fallbackGroup?.thptMajorGroups ||
          groupInfo?.thptMajorGroups ||
          [];

        // Score is already on 0-8 scale
        const rawScore = top3Scores[idx]?.score ?? item.group.score;
        const scoreVal = Math.round(rawScore);

        const cc = colorConfig[colorKey] || colorConfig.gray;
        const patternStyle = cc.pattern ? patternStyles[cc.pattern] : {};

        return (
          <div
            key={item.code}
            className='bg-white dark:bg-card-dark rounded-2xl border border-gray-100 dark:border-gray-800 shadow-xl p-6 sm:p-8 relative overflow-hidden flex flex-col'
          >
            {/* Pattern Background - using inline style for dynamic patterns */}
            <div
              className='absolute inset-0 rounded-2xl opacity-100 dark:opacity-20 pointer-events-none'
              style={patternStyle}
            />

            <div className='relative z-10'>
              {/* Header with rank badge - horizontal layout */}
              <div className='flex items-center justify-between gap-4 mb-6'>
                <div className='flex items-center gap-3'>
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-full ${cc.bg} text-white font-bold text-lg shadow-lg shrink-0`}
                  >
                    {idx + 1}
                  </div>
                  <div>
                    <h3 className={`text-lg font-bold ${cc.text} uppercase leading-tight`}>
                      {item.shortName}
                    </h3>
                    <div className='text-xs text-gray-500 dark:text-gray-400'>({item.name})</div>
                  </div>
                </div>
                <div
                  className={`inline-flex items-center gap-1.5 text-sm font-bold px-3 py-1 ${cc.bg} text-white rounded-full shadow-md shrink-0`}
                >
                  <span>Điểm:</span>
                  <span>{scoreVal}/8</span>
                </div>
              </div>

              {/* Description Section - fixed min-height for alignment */}
              <div className='mt-6 min-h-[250px]'>
                <div className={`flex items-center gap-2 mb-3 ${cc.text}`}>
                  <FileText className='w-5 h-5' />
                  <h4 className='font-bold text-base uppercase'>Mô tả chung</h4>
                </div>
                <ul
                  className={`list-disc list-outside pl-5 space-y-2 text-sm text-gray-700 dark:text-gray-300 ${cc.marker}`}
                >
                  {fullDescDetails.map((detail: { title: string; content: string }, i: number) => (
                    <li key={i}>
                      <strong>{detail.title}:</strong> {detail.content}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Career Group Suggestions Section */}
            <div className={`relative z-10 border-t border-dashed ${cc.borderDashed} pt-6 mt-6`}>
              <div className={`flex items-center gap-2 mb-3 ${cc.text}`}>
                <Briefcase className='w-5 h-5' />
                <h4 className='font-bold text-base uppercase'>{sectionTitle}</h4>
              </div>
              <p className='text-xs text-gray-600 dark:text-gray-400 mb-3 italic'>{sectionNote}</p>
              <div className='space-y-3'>
                {careerGroups.map(
                  (group: { name: string; majors?: string[]; careers?: string[] }, i: number) => {
                    const items = group.majors || group.careers || [];
                    return (
                      <div key={i} className={`p-3 ${cc.bgLight} rounded-lg border ${cc.border}`}>
                        <div className='flex items-center gap-2 mb-2'>
                          <span
                            className={`flex items-center justify-center w-5 h-5 rounded-full ${cc.bg} text-white text-xs font-bold shrink-0`}
                          >
                            {i + 1}
                          </span>
                          <span className={`text-sm font-semibold ${cc.text}`}>{group.name}</span>
                        </div>
                        <div className='pl-7 flex flex-wrap gap-1.5'>
                          {items.map((item: string, j: number) => {
                            const careerSlug = generateCareerSlug(item);
                            return (
                              <Link
                                key={j}
                                to={`/kham-pha-nghe-nghiep?career=${careerSlug}`}
                                className='text-xs px-2 py-1 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-primary/10 hover:border-primary hover:text-primary dark:hover:bg-primary/20 dark:hover:text-primary transition-colors cursor-pointer inline-flex items-center gap-1 group'
                              >
                                {item}
                                <ExternalLink className='w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity' />
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
